import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, CheckBadgeIcon, CommandLineIcon } from '@heroicons/react/24/outline';
import API_BASE_URL from '../Config/config';
import useEliteTechnologies from '../hooks/useEliteTechnologies';

const EliteSelectionModal = ({ isOpen, onClose, userId, onSelectionSuccess, currentSelection }) => {
    const [selectedTech, setSelectedTech] = useState(currentSelection || null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    // Was two hardcoded options (Java/Python only) — now the same catalog
    // the picker itself reads from (see useEliteTechnologies.js). Technologies
    // still marked "coming soon" (no content yet) are excluded here since
    // there's nothing to select into.
    const { technologies } = useEliteTechnologies();
    const selectableTechs = technologies.filter((t) => !t.isComingSoon);

    if (!isOpen) return null;

    const isAlreadySelected = !!currentSelection;

    const handleConfirm = async () => {
        if (!selectedTech) return;
        
        setIsSubmitting(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/elite/selection`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'user-id': userId
                },
                body: JSON.stringify({
                    user_id: userId,
                    selected_technology: selectedTech
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success) {
                // If the callback exists, use it.
                if (onSelectionSuccess) {
                    onSelectionSuccess(selectedTech);
                } else {
                    // Fallback to manual navigation if no callback
                    navigate(`/elite-mastery`);
                    window.location.reload();
                }
                onClose();
            } else {
                setError(data.message || 'Failed to save selection.');
            }
        } catch (err) {
            const errorMsg = err.message.includes('403') || err.message.includes('CORS')
                ? 'Access denied. Please check your subscription status.'
                : 'Network error. Please try again.';
            setError(errorMsg);
            console.error('Elite selection error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300">
            <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all border-2 border-[#5B21B6] flex flex-col max-h-[90vh]">
                <div className="h-3 bg-gradient-to-r from-[#5B21B6] via-[#7C3AED] to-[#A855F7] flex-shrink-0"></div>
                
                <button 
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>

                <div className="p-10 overflow-y-auto flex-1">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Elite Technology Mastery</h2>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left">
                            <p className="text-blue-700 font-medium mb-2">Access Rules:</p>
                            <ul className="text-sm text-blue-600 list-disc list-inside space-y-1">
                                <li>Select your primary technology to track progress.</li>
                                <li>Free users: Access to Levels 1-5 of ALL technologies.</li>
                                <li>Paid users: <span className="font-bold">Full access</span> to ALL technologies.</li>
                            </ul>
                        </div>
                        <p className="text-lg text-gray-600 max-w-lg mx-auto leading-relaxed">
                            Choose your path wisely. Which technology would you like to master?
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                        {selectableTechs.map((tech) => (
                            <div
                                key={tech.id}
                                onClick={() => setSelectedTech(tech.id)}
                                className={`relative cursor-pointer group p-6 rounded-2xl border-2 transition-all duration-300 ${
                                    selectedTech === tech.id
                                    ? 'border-[#5B21B6] bg-purple-50 ring-4 ring-purple-100 shadow-inner'
                                    : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-lg'
                                }`}
                            >
                                <div className="flex items-center gap-4 mb-4">
                                    <div className={`p-3 rounded-xl transition-colors ${
                                        selectedTech === tech.id ? 'bg-[#5B21B6] text-white' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {tech.image ? (
                                            <img src={tech.image} alt="" className="w-6 h-6 object-contain" />
                                        ) : (
                                            <CommandLineIcon className="w-6 h-6" />
                                        )}
                                    </div>
                                    <h4 className="font-bold text-xl text-gray-900">{tech.title}</h4>
                                </div>
                                <p className="text-sm text-gray-500 leading-relaxed">
                                    {tech.description}
                                </p>
                                {selectedTech === tech.id && (
                                    <div className="absolute top-4 right-4 text-[#5B21B6]">
                                        <CheckBadgeIcon className="w-6 h-6" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={handleConfirm}
                            disabled={(!selectedTech || isSubmitting) || (isAlreadySelected && selectedTech === currentSelection)}
                            className={`flex-1 py-4 px-8 rounded-xl font-bold text-white transition-all shadow-xl ${
                                (!selectedTech || isSubmitting) || (isAlreadySelected && selectedTech === currentSelection)
                                ? 'bg-gray-300 cursor-not-allowed shadow-none' 
                                : 'bg-blue-600 hover:bg-blue-700 active:scale-95 shadow-blue-200'
                            }`}
                        >
                            {isSubmitting ? 'Processing...' : isAlreadySelected ? 'Continue Learning' : 'Confirm My Selection'}
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 py-4 px-8 rounded-xl font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EliteSelectionModal;
